<script lang="ts">
    import { onMount } from "svelte";
    import { Dialog, NoData } from "@/components";
    import { translate } from "@/utils/format";
    import { stripUrlParams } from "../lib/sections";
    import { formatDay, formatMoney } from "../lib/format";
    import { statusLabel } from "../lib/sponsoredCopy";
    import { getDashboardCtx } from "../state/dashboard.svelte";
    import { getSponsoredCtx } from "../state/sponsored.svelte";
    import SectionHeader from "../components/SectionHeader.svelte";
    import AddSponsoredModal from "./sponsored/AddSponsoredModal.svelte";
    import type { SponsoredSubscription } from "@/services/sponsored";

    const store = getDashboardCtx();
    const sponsored = getSponsoredCtx();

    let showAdd = $state(false);
    let toCancel = $state<SponsoredSubscription | null>(null);

    const money = (cents: number, cur: string) =>
        formatMoney(cents, cur, store.uiLocale);

    onMount(() => {
        const billing = new URLSearchParams(window.location.search).get(
            "billing",
        );
        if (billing === "success" || billing === "cancel") {
            stripUrlParams(["billing"]);
            void sponsored.onBillingReturn(billing);
        } else {
            void sponsored.load();
        }
    });

    function endLabel(s: SponsoredSubscription) {
        const date = formatDay(s.currentPeriodEnd, store.uiLocale);
        return s.status === "canceling" || s.status === "canceled"
            ? `${translate("PARTNER_SPONSORED_ENDS", "Finaliza el")} ${date}`
            : `${translate("PARTNER_SPONSORED_RENEWS", "Renueva el")} ${date}`;
    }

    async function confirmCancel() {
        const s = toCancel;
        toCancel = null;
        if (!s) return;
        const ok = await sponsored.cancel(s.id);
        if (ok) {
            store.toast(
                translate(
                    "PARTNER_SPONSORED_CANCELED_TOAST",
                    "Cancelada. Mantiene el acceso hasta el final del periodo.",
                ),
            );
        }
    }
</script>

<div class="dashStack">
    <SectionHeader id="sponsored">
        <button
            type="button"
            class="button --primary"
            onclick={() => (showAdd = true)}
        >
            <i class="fal fa-plus" aria-hidden="true"></i>
            {translate("PARTNER_SPONSORED_ADD", "Pagar a un usuario")}
        </button>
    </SectionHeader>

    {#if sponsored.error}
        <div class="referralsPanel">
            <p class="sponsorModal__error" role="alert">{sponsored.error}</p>
            <button type="button" class="button" onclick={sponsored.refresh}>
                {translate("PARTNER_RETRY", "Reintentar")}
            </button>
        </div>
    {:else if sponsored.loading && !sponsored.loaded}
        <div class="referralsPanel" aria-busy="true" aria-live="polite">
            <span class="sr-only">
                {translate("LOADING_CONTENT", "Cargando tu contenido...")}
            </span>
            <div class="skeletonRows" aria-hidden="true">
                <span class="skeleton"></span>
                <span class="skeleton"></span>
                <span class="skeleton"></span>
            </div>
        </div>
    {:else if !sponsored.billing.hasDefaultPaymentMethod && !sponsored.visible.length}
        <div class="referralsEmpty">
            <h5>
                {translate(
                    "PARTNER_SPONSORED_EMPTY_TITLE",
                    "Paga la suscripción Caloo de tus usuarios",
                )}
            </h5>
            <p>
                {translate(
                    "PARTNER_SPONSORED_EMPTY_DESC",
                    "Guarda un método de pago y elige a quién quieres regalar la suscripción. Tú decides cuándo darla de baja; la persona mantiene el acceso hasta el final del periodo pagado.",
                )}
            </p>
            <button
                type="button"
                class="button --primary"
                disabled={sponsored.portalBusy}
                onclick={sponsored.startSetup}
            >
                {translate("PARTNER_BILLING_ADD", "Añadir método de pago")}
            </button>
        </div>
    {:else}
        <div class="kpiGrid kpiGrid--3">
            <div class="kpi">
                <span class="kpi__value">
                    {store.formatCount(sponsored.totals.active)}
                </span>
                <span class="kpi__label">
                    {translate("PARTNER_SPONSORED_KPI_ACTIVE", "Suscripciones que pagas")}
                </span>
            </div>
            <div class="kpi">
                <span class="kpi__value">
                    {money(
                        sponsored.totals.monthlyCost.amount,
                        sponsored.totals.monthlyCost.currency,
                    )}
                </span>
                <span class="kpi__label">
                    {translate("PARTNER_SPONSORED_KPI_COST", "Coste mensual")}
                </span>
            </div>
            <div class="kpi">
                <span class="kpi__value">
                    {store.formatCount(sponsored.totals.canceling)}
                </span>
                <span class="kpi__label">
                    {translate("PARTNER_SPONSORED_KPI_CANCELING", "Se cancelan al final del periodo")}
                </span>
            </div>
        </div>

        {#if sponsored.totals.pastDue > 0}
            <div class="sponsorNotice" role="status">
                <i class="fal fa-exclamation-circle" aria-hidden="true"></i>
                <span>
                    {translate(
                        "PARTNER_SPONSORED_PAST_DUE_NOTICE",
                        "Hay pagos pendientes. Actualiza tu método de pago para no perder el acceso de esos usuarios.",
                    )}
                </span>
                <button
                    type="button"
                    class="button"
                    disabled={sponsored.portalBusy}
                    onclick={sponsored.openPortal}
                >
                    {translate("PARTNER_SPONSORED_FIX_PAYMENT", "Actualizar método de pago")}
                </button>
            </div>
        {/if}

        <div class="referralsPanel">
            {#if sponsored.visible.length}
                <div class="tableLike sponsorTable" role="table" aria-label={translate("PARTNER_NAV_SPONSORED", "Pagadas por ti")}>
                    <div class="tableLike__head sponsorTable__row" role="row">
                        <span role="columnheader">{translate("PARTNER_SPONSORED_COL_USER", "Usuario")}</span>
                        <span role="columnheader">{translate("PARTNER_SPONSORED_COL_STATUS", "Estado")}</span>
                        <span role="columnheader">{translate("PARTNER_SPONSORED_COL_STARTED", "Alta")}</span>
                        <span role="columnheader">{translate("PARTNER_SPONSORED_COL_PERIOD", "Periodo")}</span>
                        <span class="right" role="columnheader">
                            {translate("PARTNER_SPONSORED_COL_AMOUNT", "Importe")}
                        </span>
                        <span class="right" role="columnheader"></span>
                    </div>
                    <div class="tableLike__body" role="rowgroup">
                        {#each sponsored.visible as s (s.id)}
                            <div class="tableLike__row sponsorTable__row" role="row">
                                <span class="mono sponsorTable__user" role="cell">{s.emailMasked}</span>
                                <span role="cell">
                                    <span class="badge badge--{s.status}">
                                        {statusLabel(s.status)}
                                    </span>
                                </span>
                                <span class="mono" role="cell">
                                    {formatDay(s.startedAt, store.uiLocale)}
                                </span>
                                <span class="mono" role="cell">{endLabel(s)}</span>
                                <span class="mono right" role="cell">
                                    {money(s.amount.amount, s.amount.currency)}
                                </span>
                                <span class="right sponsorTable__actions" role="cell">
                                    {#if s.status === "canceling"}
                                        <button
                                            type="button"
                                            class="button"
                                            disabled={sponsored.busyIds.includes(s.id)}
                                            onclick={() => sponsored.reactivate(s.id)}
                                        >
                                            {translate("PARTNER_SPONSORED_REACTIVATE", "Reactivar")}
                                        </button>
                                    {:else if s.status === "past_due"}
                                        <button
                                            type="button"
                                            class="button"
                                            disabled={sponsored.portalBusy}
                                            onclick={sponsored.openPortal}
                                        >
                                            {translate("PARTNER_SPONSORED_FIX", "Resolver")}
                                        </button>
                                    {:else if s.status === "active"}
                                        <button
                                            type="button"
                                            class="button --cancel"
                                            disabled={sponsored.busyIds.includes(s.id)}
                                            onclick={() => (toCancel = s)}
                                        >
                                            {translate("PARTNER_SPONSORED_CANCEL", "Dar de baja")}
                                        </button>
                                    {/if}
                                </span>
                            </div>
                        {/each}
                    </div>
                </div>
            {:else}
                <NoData
                    params={{
                        icon: "fal fa-hand-holding-heart",
                        title: translate(
                            "PARTNER_SPONSORED_NONE_TITLE",
                            "Aún no pagas ninguna suscripción",
                        ),
                        description: translate(
                            "PARTNER_SPONSORED_NONE_DESC",
                            "Elige a un cliente o escribe su email para empezar.",
                        ),
                    }}
                />
            {/if}
        </div>
    {/if}
</div>

<AddSponsoredModal open={showAdd} onClose={() => (showAdd = false)} />

<Dialog
    isOpen={!!toCancel}
    title={translate("PARTNER_SPONSORED_CANCEL_TITLE", "¿Dar de baja esta suscripción?")}
    message={translate(
        "PARTNER_SPONSORED_CANCEL_MSG",
        "Dejarás de pagarla. La persona mantiene el acceso hasta el final del periodo ya pagado y puedes reactivarla antes de esa fecha.",
    )}
    labelOk={translate("PARTNER_SPONSORED_CANCEL", "Dar de baja")}
    labelCancel={translate("PARTNER_SPONSORED_KEEP", "Mantener")}
    btnType="--delete"
    onOk={confirmCancel}
    onCancel={() => (toCancel = null)}
/>
